import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { professionalData } from '../professionalData';

const ResearchPage = () => {
  const [thesisExpanded, setThesisExpanded] = useState(false);
  const [publicationExpanded, setPublicationExpanded] = useState(false);
  const [carnegieExpanded, setCarnegieExpanded] = useState(false);
  const [psychophysExpanded, setPsychophysExpanded] = useState(false);
  const [workplaceExpanded, setWorkplaceExpanded] = useState(false);

  return (
    <div>
      {/* Hero */}
      <section className="section" style={{ paddingTop: '4rem', paddingBottom: '4rem' }} data-testid="research-hero">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
              Research & Innovation
            </p>
            <h1 style={{ marginBottom: '1.5rem' }}>
              Advancing Neuro-Psychology
            </h1>
            <p style={{ 
              fontSize: '1.25rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)'
            }}>
              I leverage computational tools, psychophysiological data, and neuro-affirming frameworks to bridge the gap between clinical practice and evidence-based science. I am currently open to collaborations for research and innovation in pediatric mental health.
            </p>
          </div>
        </div>
      </section>

      {/* Computational Psychology */}
      <section className="section-alt" data-testid="computational-section">
        <div className="container">
          <div className="glass-card" style={{ maxWidth: '1000px', margin: '0 auto', padding: '3rem' }}>
            <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              Computational Psychology
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              textAlign: 'center',
              marginBottom: '2rem'
            }}>
              Highly proficient in leveraging clinical data pipelines to drive objective clinical practice
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem',
              marginTop: '2rem'
            }}>
              {professionalData.technicalSkills.slice(0, 6).map((skill, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    padding: '1rem',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: 'var(--primary)'
                  }}
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Lab - Publications */}
      <section className="section" data-testid="publications-section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2>Research Lab</h2>
            <p style={{ 
              fontSize: '1.125rem',
              color: 'var(--text-muted)',
              marginTop: '1rem'
            }}>
              Published research & academic contributions
            </p>
          </div>

          {/* Thesis/Publication 1 */}
          <div className="glass-card" style={{ 
            maxWidth: '1000px', 
            margin: '0 auto 2rem',
            padding: '2.5rem'
          }}>
            <div style={{ 
              display: 'inline-block',
              background: 'var(--accent)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              PUBLISHED RESEARCH
            </div>
            <h3 style={{ 
              fontSize: '1.5rem',
              marginBottom: '1rem',
              lineHeight: 1.4,
              fontFamily: 'var(--font-body)',
              fontWeight: 600
            }}>
              {professionalData.research.thesis.title}
            </h3>
            <p style={{ 
              fontSize: '1rem',
              color: 'var(--text-muted)',
              marginBottom: '1rem'
            }}>
              <strong>{professionalData.research.thesis.journal}</strong> ({professionalData.research.thesis.year})
            </p>
            <p style={{ 
              fontSize: '1rem',
              color: 'var(--text-muted)',
              marginBottom: '1.5rem'
            }}>
              <strong>ORCID:</strong>{' '}
              <a 
                href={`https://orcid.org/${professionalData.research.orcid}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent)', textDecoration: 'underline' }}
              >
                {professionalData.research.orcid}
              </a>
            </p>

            {/* Expandable Abstract */}
            {thesisExpanded && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)',
                  marginBottom: '1rem'
                }}>
                  <strong>Abstract:</strong> Spearheaded specialized translational research utilizing the Facial Action Coding System (FACS) and OpenFace 2.0 to conduct differential analyses of micro-emotional expressions and neurological responses in autistic versus neurotypical pediatric populations during naturalistic interactions.
                </p>
                <p style={{ 
                  fontSize: '0.95rem',
                  color: 'var(--text-muted)'
                }}>
                  <strong>Methodology:</strong> {professionalData.research.thesis.methodology}
                </p>
              </div>
            )}

            <button
              onClick={() => setThesisExpanded(!thesisExpanded)}
              style={{
                background: 'transparent',
                border: '2px solid var(--accent)',
                color: 'var(--accent)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              data-testid="thesis-know-more"
            >
              {thesisExpanded ? 'Show Less' : 'Know More'}
              {thesisExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          {/* Publication 2 */}
          <div className="glass-card" style={{ 
            maxWidth: '1000px', 
            margin: '0 auto',
            padding: '2.5rem'
          }}>
            <div style={{ 
              display: 'inline-block',
              background: 'var(--primary)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              PUBLISHED PAPER
            </div>
            <h3 style={{ 
              fontSize: '1.5rem',
              marginBottom: '1rem',
              lineHeight: 1.4,
              fontFamily: 'var(--font-body)',
              fontWeight: 600
            }}>
              Exploration of Symptoms of OCD and Anxiety in Association with Fear of COVID-19: A Global Cross-Sectional Study
            </h3>
            <p style={{ 
              fontSize: '1rem',
              color: 'var(--text-muted)',
              marginBottom: '1.5rem'
            }}>
              <strong>Indian Journal of Public Health Research & Development</strong> (Vol. 17, Issue 2, 2026)
            </p>

            {/* Expandable Description */}
            {publicationExpanded && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)',
                  marginBottom: '1rem'
                }}>
                  <strong>Abstract:</strong> The COVID-19 pandemic has caused extensive emotional suffering. This study investigates whether fear of COVID-19 is connected with symptoms of obsessive-compulsive disorder (OCD) and anxiety in a worldwide sample, primarily from India. OCD and anxiety symptoms were shown to have a substantial association (r =.598, p &lt;.01). The findings highlight the necessity for culturally customized mental health therapies to address co-occurring anxiety and OCD during pandemics.
                </p>
                <p style={{ 
                  fontSize: '0.95rem',
                  color: 'var(--text-muted)'
                }}>
                  <strong>Research Focus:</strong> Large-scale behavioral data analysis exploring the acute psychopathological intersections of OCD, anxiety, and distress tolerance architectures.
                </p>
              </div>
            )}

            <button
              onClick={() => setPublicationExpanded(!publicationExpanded)}
              style={{
                background: 'transparent',
                border: '2px solid var(--accent)',
                color: 'var(--accent)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--accent)';
              }}
              data-testid="publication-know-more"
            >
              {publicationExpanded ? 'Show Less' : 'Know More'}
              {publicationExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          {/* NEW CARD 3: Carnegie Mellon - Data Analytics */}
          <div className="glass-card" style={{ 
            maxWidth: '1000px', 
            margin: '2rem auto',
            padding: '2.5rem'
          }}>
            <div style={{ 
              display: 'inline-block',
              background: 'var(--primary)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              DATA ANALYTICS & COGNITIVE PIPELINES
            </div>
            <h3 style={{ 
              fontSize: '1.5rem',
              marginBottom: '1rem',
              lineHeight: 1.4,
              fontFamily: 'var(--font-body)',
              fontWeight: 600
            }}>
              Developmental Psychology Research Assistant
            </h3>
            <p style={{ 
              fontSize: '1rem',
              color: 'var(--text-muted)',
              marginBottom: '1.5rem'
            }}>
              <strong>Carnegie Mellon University</strong> (May 2017 – Jun 2017)
            </p>

            {carnegieExpanded && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)'
                }}>
                  <strong>Summary:</strong> Partnered under world-leading cognitive labs to design and pilot mathematical cognition packets across Pittsburgh public schools. Applied R programming for cognitive task analysis, behavioral index mapping, and real-time visualization of structural numeric magnitude errors in childhood development.
                </p>
              </div>
            )}

            <button
              onClick={() => setCarnegieExpanded(!carnegieExpanded)}
              style={{
                background: 'transparent',
                border: '2px solid var(--accent)',
                color: 'var(--accent)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--accent)';
              }}
            >
              {carnegieExpanded ? 'Show Less' : 'Know More'}
              {carnegieExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          {/* NEW CARD 4: BGSU Psychophysiology */}
          <div className="glass-card" style={{ 
            maxWidth: '1000px', 
            margin: '2rem auto',
            padding: '2.5rem'
          }}>
            <div style={{ 
              display: 'inline-block',
              background: 'var(--primary)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              PSYCHOPHYSIOLOGY & EMOTIONAL PROCESSING
            </div>
            <h3 style={{ 
              fontSize: '1.5rem',
              marginBottom: '1rem',
              lineHeight: 1.4,
              fontFamily: 'var(--font-body)',
              fontWeight: 600
            }}>
              Psychophysiology Lab Research Assistant
            </h3>
            <p style={{ 
              fontSize: '1rem',
              color: 'var(--text-muted)',
              marginBottom: '1.5rem'
            }}>
              <strong>Bowling Green State University</strong> (Jan 2017 – May 2017)
            </p>

            {psychophysExpanded && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)'
                }}>
                  <strong>Summary:</strong> Employed psychophysiological monitoring to investigate emotion-behavior links in disordered eating. Conducted inferential data analysis using SAS, integrating multi-factorial behavioral data sets, and drafted results summaries for cross-functional dissemination within lab teams.
                </p>
              </div>
            )}

            <button
              onClick={() => setPsychophysExpanded(!psychophysExpanded)}
              style={{
                background: 'transparent',
                border: '2px solid var(--accent)',
                color: 'var(--accent)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--accent)';
              }}
            >
              {psychophysExpanded ? 'Show Less' : 'Know More'}
              {psychophysExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>

          {/* NEW CARD 5: BGSU Workplace Behavior */}
          <div className="glass-card" style={{ 
            maxWidth: '1000px', 
            margin: '2rem auto',
            padding: '2.5rem'
          }}>
            <div style={{ 
              display: 'inline-block',
              background: 'var(--primary)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              CONSTRUCT VALIDATION & WORKPLACE BEHAVIOR
            </div>
            <h3 style={{ 
              fontSize: '1.5rem',
              marginBottom: '1rem',
              lineHeight: 1.4,
              fontFamily: 'var(--font-body)',
              fontWeight: 600
            }}>
              Workplace Behavior Research Assistant
            </h3>
            <p style={{ 
              fontSize: '1rem',
              color: 'var(--text-muted)',
              marginBottom: '1.5rem'
            }}>
              <strong>Bowling Green State University</strong> (Nov 2016 – May 2017)
            </p>

            {workplaceExpanded && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '2rem',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <p style={{ 
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: 'var(--text-main)'
                }}>
                  <strong>Summary:</strong> Managed SPSS-based data pipelines and supported construct validation of workplace deviance metrics. Assisted in the psychometric evaluation and development of behavioral indices, synthesizing complex statistical findings into reports for academic publication.
                </p>
              </div>
            )}

            <button
              onClick={() => setWorkplaceExpanded(!workplaceExpanded)}
              style={{
                background: 'transparent',
                border: '2px solid var(--accent)',
                color: 'var(--accent)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'var(--accent)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--accent)';
              }}
            >
              {workplaceExpanded ? 'Show Less' : 'Know More'}
              {workplaceExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>
      </section>

      {/* Parvarish AI Innovation */}
      <section className="section-alt" data-testid="parvarish-section">
        <div className="container">
          <div className="glass-card-strong" style={{ 
            maxWidth: '1000px', 
            margin: '0 auto',
            padding: '3rem'
          }}>
            <div style={{ 
              display: 'inline-block',
              background: '#FFA500',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              BETA VERSION
            </div>
            <h2 style={{ marginBottom: '1rem' }}>
              {professionalData.parvarishAI.title}
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              marginBottom: '2rem'
            }}>
              {professionalData.parvarishAI.description}
            </p>
            <a 
              href={professionalData.parvarishAI.link}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              data-testid="parvarish-link"
            >
              Try Parvarish AI
              <ExternalLink size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Collaboration CTA */}
      <section className="section" data-testid="collaboration-section">
        <div className="container">
          <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>
              Open for Collaboration
            </h2>
            <p style={{ 
              fontSize: '1.125rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              marginBottom: '2rem'
            }}>
              Are you a researcher or academic organization looking to advance the standard of neuro-psychology? I am actively seeking collaborations utilizing FACS, predictive behavioral modeling, and cross-cultural developmental psychology.
            </p>
            <Link
              to="/contact"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1.125rem'
              }}
            >
              Submit Research Inquiry
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchPage;
